<template>
  <b-overlay :show="isLoading" rounded="sm" :opacity="0.5">
    <b-alert variant="danger" show dismissible v-show="errors.length > 0">
      <li v-bind:key="index" v-for="(error, index) in errors">{{ error }}</li>
    </b-alert>
    <b-row class="ml-2">
      <b-col>
        <b-row class="mt-2 flex-row flex-nowrap">
          <b-col class="d-flex">
            <b-button
              v-for="(competency, index) in competencies"
              :key="`a-${index}`"
              ref="btnIndicators"
              variant="info"
              @click="getIndicators(competency, index)"
              class="btn-hover mx-1"
              :class="{ 'btn-active': buttonActiveIndex === index }"
            >
              <span class="text-success">
                <mdicon
                  v-if="competency.score !== null"
                  name="check-circle"
                  size="20"
                />
              </span>
              <span
                v-text="pathOr(null, ['competency', 'caption'], competency)"
              />
            </b-button>
          </b-col>
        </b-row>
        <b-row class="my-2">
          <b-col>
            <b-card v-show="competency_selected">
              <MarkingCompetencyIndicators
                :center_execute_id="+center_execute_id"
                :case_id="+case_id"
                :competency="competency_selected"
                :isScoreEditable="isScoreEditable"
                :isEditable="isEditable"
                :userExecutiveRoleId="userExecutiveRoleId"
                ref="markingCompetencyIndicators"
                @getFinalEvidance="getFinalEvidance"
              />
              <b-row>
                <b-col md="6" cols="12">
                  <b-form-group
                    :label="$lang.calculated_score"
                    label-for="address"
                    :label-cols="this.$config.form.labelCol"
                  >
                    <requiredComponent
                      slot="label"
                      :label="$lang.calculated_score"
                      :rules="$v.calculated_score.$params"
                    />
                    <b-input-group>
                      <b-form-input
                        id="postal_code"
                        type="text"
                        v-model="$v.calculated_score.$model"
                        :state="
                          $v.calculated_score.$dirty
                            ? !$v.calculated_score.$error
                            : null
                        "
                        :autocomplete="$lang.calculated_score"
                        class="text-right"
                        disabled
                      ></b-form-input>
                      <b-input-group-append>
                        <b-button
                          type="submit"
                          class="br-25"
                          size="md"
                          variant="success"
                          @click="calculateScore"
                          :disabled="
                            calculateLoading || !isEditable || !isScoreEditable
                          "
                        >
                          <template v-if="calculateLoading">
                            <b-spinner small></b-spinner>
                            <span class="sr-only">Loading...</span>
                          </template>
                          <template v-if="!calculateLoading">
                            {{ $lang.calculate_score }}
                            <span><i class="fas fa-calculator"></i></span>
                          </template>
                        </b-button>
                      </b-input-group-append>
                      <b-form-invalid-feedback id="name-feedback">
                        {{
                          $lang.error_required | replace($lang.calculate_score)
                        }}
                      </b-form-invalid-feedback>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col md="6" cols="12">
                  <b-form-group
                    :label="$lang.score_selected"
                    label-for="address"
                    :label-cols="this.$config.form.labelCol"
                  >
                    <requiredComponent
                      slot="label"
                      :label="$lang.score_selected"
                      :rules="$v.score_selected.$params"
                    />
                    <selectFind
                      :options="score_options"
                      v-model="$v.score_selected.$model"
                      label="text"
                      id="value"
                      :isValid="
                        $v.score_selected.$dirty
                          ? !$v.score_selected.$error
                          : null
                      "
                      :loading="scoreLoading"
                      :disabled="!isEditable || !isScoreEditable"
                      @input="changeScoreSelected"
                    />
                    <b-form-invalid-feedback id="name-feedback"
                      >{{
                        $lang.error_required | replace($lang.score_selected)
                      }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col xs="12" md="12">
                  <requiredComponent
                    slot="label"
                    :label="$lang.other_tips_explanations"
                    :rules="$v.description.$params"
                  />
                  <Editor
                    :rows="4"
                    :cols="50"
                    dir="rtl"
                    v-model="description"
                    :config="editorConfig"
                    :resource="``"
                  >
                    <br />
                  </Editor>
                </b-col>
              </b-row>
              <b-row>
                <b-col xs="12" md="12" class="my-2 d-none">
                  <div>
                    <div class="my-1">
                      {{ $lang.finalOverall }}
                      <b-button
                        variant="primary"
                        size="sm"
                        :disabled="isloading || !isEditable || !isScoreEditable"
                        @click="getFinalEvidance"
                      >
                        <b-spinner small v-if="isloading" />
                        <span v-else v-text="$lang.ProduceFinaltext" />
                      </b-button>
                    </div>

                    <b-form-input
                      hidden
                      v-model="$v.evidanceFinalText.model"
                      :state="
                        $v.evidanceFinalText.$dirty
                          ? !$v.evidanceFinalText.$error
                          : null
                      "
                    />
                    <b-form-invalid-feedback id="name-feedback"
                      >{{ $lang.error_required | replace($lang.finalOverall) }}
                    </b-form-invalid-feedback>
                  </div>
                  <div
                    class="card-description p-3"
                    v-if="evidanceFinalText"
                    v-html="evidanceFinalText"
                  />
                </b-col>
              </b-row>
              <div slot="footer" class="form-group form-btn">
                <b-button
                  type="submit"
                  variant="success"
                  @click.prevent="save"
                  :disabled="!isEditable"
                >
                  <mdicon name="check" size="20" />
                  {{ $lang.submitButton }}
                </b-button>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
      <Confirm
        :title="modalTitle"
        :description="message"
        :loading="isLoading"
        :loadingSpinnerText="$lang.updating_data"
        v-model="confirmModal"
        :ok-title="$lang.yes"
        :cancel-title="$lang.no"
        @ok="okConfirm"
        @cancel="cancelConfirm"
      />
    </b-row>
  </b-overlay>
</template>

<script>
import Editor from "@/components/ckeditor/editor";
import Confirm from "@/components/base/confirm";
import MarkingCompetencyIndicators from "./markingCompetencyIndicators";
import { required } from "vuelidate/lib/validators";
import { addAttachmentCode } from "@/components/ckeditor/submitAttachment";
import { pathOr, includes } from "ramda";

export default {
  name: "marking-competency",
  inject: [
    "centerExecuteCompetencyRepository",
    "centerExecuteCompetencySuggestScoreRepository",
    "centerExecuteCaseFinalTextRepository",
  ],
  components: {
    MarkingCompetencyIndicators,
    Editor,
    Confirm,
  },
  validations: {
    score_selected: {
      required,
    },
    description: {},
    evidanceFinalText: {},
    calculated_score: {
      required,
    },
  },
  props: {
    case_id: {
      type: [String, Number],
      default: null,
    },
    center_execute_id: {
      type: [String, Number],
      default: null,
    },
    competencies: {
      type: Array,
      default: () => [],
    },
    score_options: {
      type: Array,
      default: () => [],
    },
    case_status: {
      type: Object,
      default: () => {},
    },
    isScoreEditable: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    userExecutiveRoleId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isActive: false,
      isloading: false,
      confirmModal: false, // بازکردن مدال کانفرم
      modalTitle: "", // عنوان مدال
      message: "", // نمایش متن حذف فایل
      competency_selected: null,
      calculated_score: null,
      score_selected: null,
      evidanceFinalText: null,
      scoreLoading: false,
      score_selected_id: null,
      editor: "ClassicEditor",
      errors: [],
      editorConfig: {
        toolbar: [
          "heading",
          "|",
          "bold",
          "italic",
          "link",
          "bulletedList",
          "numberedList",
          "blockQuote",
        ],
        // The configuration of the editor.
        language: {
          // language: fa,
          // The UI will be fa.
          ui: "fa",
          // But the content will be edited in fa.
          content: "fa",
        },
      },
      description: null,
      isLoading: false,
      calculateLoading: false,
      debounceGet: null,
      buttonActiveIndex: 0,
    };
  },
  watch: {
    score_options() {
      this.score_selected = this.score_options.find(
        (x) => x.value === this.score_selected_id
      );
    },
    description() {
      this.setDelay(() => {
        this.getFinalEvidance();
      });
    },
    competencies(data) {
      this.getFirstCompetency(data);
    },
  },
  created() {
    this.getFirstCompetency(this.competencies);
  },
  methods: {
    pathOr,
    includes,
    getFirstCompetency(competencies) {
      if (this.competency_selected) return;

      const competency = pathOr(null, ["0"], competencies);
      if (competency) {
        this.getIndicators(competency, this.buttonActiveIndex);
      }
    },
    async changeScoreSelected(item) {
      try {
        this.isLoading = true;
        let payload = {
          score: item.value,
        };
        const res = await this.centerExecuteCompetencyRepository.partial_edit(
          this.center_execute_id,
          this.case_id,
          this.competency_selected.id,
          payload
        );
        this.$notify.success(res.data.message);
        if (!this.calculated_score) {
          await this.calculateScore();
        }
        await this.getFinalEvidance();
        await this.save();
        this.$emit("competencyUpdated", false);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.isLoading = false;
      }
    },
    cancelConfirm() {
      this.confirmModal = false;
      this.modalTitle = "";
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      if (
        pathOr(null, ["value"], this.score_selected) &&
        !includes(this.calculated_score, [null, 0])
      ) {
        await this.okConfirm();
      } else {
        this.confirmModal = true;
        this.message = this.$lang.confirm_competency;
        this.modalTitle = this.$lang.case_confirm;
      }
    },
    async okConfirm(allowAddAttachmentCode = true) {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      try {
        this.isLoading = true;
        const payload = {
          score: pathOr(null, ["value"], this.score_selected),
          description: this.description,
          final_description: this.evidanceFinalText,
          score_suggest:
            this.calculated_score === "w" ? 0 : this.calculated_score,
        };
        const response = await this.centerExecuteCompetencyRepository.edit(
          this.center_execute_id,
          this.case_id,
          this.competency_selected.id,
          payload
        );
        if (allowAddAttachmentCode) {
          const attachmentResponse = await addAttachmentCode(
            "competency",
            this.competency_selected.id,
            this.description
          );
          const failCodes = pathOr(null, ["fail_codes"], attachmentResponse);
          const description = pathOr(null, ["description"], attachmentResponse);
          if (failCodes) {
            this.description = description;
            this.save(false);
          }
        }
        // this.$emit("competencyUpdated");
        this.$notify.success(response.data.message);
        this.cancelConfirm();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getFinalEvidance() {
      const scoreSelected = pathOr(null, ["value"], this.score_selected);
      if (scoreSelected === null) return;
      if (scoreSelected >= 0 || scoreSelected === "w") {
        try {
          this.isloading = true;
          const payload = {
            score: scoreSelected,
            description: this.description,
          };
          const response =
            await this.centerExecuteCaseFinalTextRepository.partial_edit(
              this.center_execute_id,
              this.case_id,
              this.competency_selected.id,
              payload
            );
          this.evidanceFinalText = pathOr(null, ["data", "data"], response);
        } catch (error) {
          this.$generateError(error);
        } finally {
          this.isloading = false;
        }
      } else {
        this.$notify.error(this.$lang.error_score_competency);
      }
    },
    getIndicators(competency, index) {
      this.buttonActiveIndex = index;
      this.score_selected_id = pathOr(null, ["score"], competency);
      const scoreSuggest =
        competency.score_suggest === 0 ? competency.score_suggest : null;
      this.calculated_score = competency.score_suggest || scoreSuggest;
      this.competency_selected = null;
      this.evidanceFinalText = pathOr(null, ["final_description"], competency);
      this.competency_selected = competency.competency;
      this.description = pathOr("", ["description"], competency);
      this.score_selected = this.score_options.find((x) => {
        const score =
          this.score_selected_id === "w" ? 0 : this.score_selected_id;
        return x.value === score;
      });
    },
    async calculateScore() {
      this.calculateLoading = true;
      try {
        const response =
          await this.centerExecuteCompetencySuggestScoreRepository.list(
            this.center_execute_id,
            this.case_id,
            this.competency_selected.id
          );
        this.calculated_score = pathOr(
          null,
          ["data", "data", "score"],
          response
        );
        await this.getFinalEvidance();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.calculateLoading = false;
      }
    },
    setDelay(callback) {
      if (this.debounceGet) {
        clearTimeout(this.debounceGet);
        this.debounceGet = null;
      }
      this.debounceGet = setTimeout(() => {
        callback();
      }, 800);
    },
  },
};
</script>

<style lang="scss">
.white-space-auto {
  display: flex;
  white-space: nowrap;
  overflow: auto;
}
.btn-active {
  background-color: rgb(16, 115, 150);
  color: white;
  border: 1px solid;
}
.btn-hover:hover {
  background-color: rgb(16, 115, 150);
  color: white;
  border: 1px solid;
}
.card-description {
  border: 1px solid lightgray;
  min-height: 100px;
}
.br-25 {
  border-radius: 0.25rem !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.evidanceFinal > figure > img {
  width: 100% !important;
}
.evidence_blue {
  color: #20a8d8 !important;
}
</style>
